/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Shared helpers for C2PA hashed-URI verification (§8.4.2.3, §15.4).
 *
 * `hashJumbfBox` computes the hash of a JUMBF structure exactly as §8.4.2.3
 * prescribes: over the contents of the JUMBF superbox (Description Box + all
 * content boxes) but NOT the 8-byte superbox header — which is precisely the
 * `JumbfBox.range` the parser records (`start` already skips the header). Used
 * by assertion hashed-URI checks (§15.10) and the ingredient manifest-box hash
 * (§15.11).
 *
 * `strictDigestName` resolves the C2PA hashing algorithm (§15.4) to a WebCrypto
 * digest name with NO silent default — an unknown/absent alg returns null so the
 * caller can reject with `algorithm.unsupported`, rather than validating a forged
 * assertion against SHA-256. Shared with the hard-binding alg-strictness check (§15.4).
 */

import type { JumbfBox } from '../jumbf/parseJumbf'
import { webcryptoDigestForC2pa } from '../bmff/bmffHash'

/**
 * The strict §15.4/§13.1 hash-algorithm resolver (WebCrypto digest name, or null for
 * an absent/unsupported alg → caller emits `algorithm.unsupported`). Canonical
 * implementation lives in `bmff/bmffHash.ts`; re-exported here under the name the
 * verify layer uses.
 */
export const strictDigestName = webcryptoDigestForC2pa

/**
 * Hashes the JUMBF superbox contents per §8.4.2.3 (`box.range` = payload after the
 * 8-byte superbox header). `buffer` is the JUMBF manifest-store buffer the box was
 * parsed from; `algName` is a WebCrypto digest name (e.g. `SHA-256`).
 */
export async function hashJumbfBox (buffer: Uint8Array, box: JumbfBox, algName: string): Promise<Uint8Array> {
    const slice = buffer.subarray(box.range.start, box.range.end)
    const digest = await crypto.subtle.digest(algName, slice as unknown as ArrayBuffer)
    return new Uint8Array(digest)
}

/** Constant-shape byte comparison for hash equality. */
export function bytesEqual (a: Uint8Array | undefined, b: Uint8Array | undefined): boolean {
    if (!a || !b || a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
    return true
}
